import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const ValueCard = ({ title, description }) => {
  return (
    <div className="relative py-8">
      <div className="text-center">
        <motion.h3
          className="mb-4 text-abyssal-blue"
          initial={{ opacity: 0, x: -60 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.7, ease: [0.25, 0.46, 0.45, 0.94] }}
          viewport={{ once: true, margin: '-50px' }}
          style={{
            fontFamily: "'Novecento Sans Wide', 'Arial', sans-serif",
            fontSize: 'clamp(1.2rem, 2.5vw, 1.6rem)',
            fontWeight: 600,
            letterSpacing: '0.03em',
            textTransform: 'uppercase'
          }}
        >
          {title}
        </motion.h3>
        <p
          className="text-steel-grey max-w-2xl mx-auto"
          style={{
            fontFamily: "'Eurostile', 'Arial', sans-serif",
            fontSize: 'clamp(1rem, 1.5vw, 1.15rem)',
            lineHeight: 1.8
          }}
        >
          {description}
        </p>
      </div>
    </div>
  );
};

export const Approach = () => {
  const { language } = useLanguage();
  const bannerRef = useRef(null);
  const imageRef = useRef(null);
  const introSectionRef = useRef(null);
  const introTextRef = useRef(null);
  const transitionRef = useRef(null);
  const contentWrapperRef = useRef(null);
  const valuesTransitionRef = useRef(null);

  const textParagraphs = language === 'fr'
    ? [
        "Là où les systèmes se tendent, où les contraintes s'accumulent et où les décisions prennent un poids déterminant.",
        "Ces zones demandent de la précision, de la lucidité et une compréhension fine des réalités du terrain.",
        "C'est dans ces environnements qu'Ocean Peak Group intervient. Nous agissons avec la responsabilité d'un actionnaire et l'expérience d'équipes opérationnelles qui ont dirigé des usines, stabilisé des flux et traversé des situations complexes. Cette expérience ne nous donne pas des certitudes, mais une façon d'aborder les organisations avec respect, pragmatisme et justesse.",
        "Notre rôle : éclairer ce qui est difficile à lire, restaurer les repères, renforcer les fondamentaux et construire, aux côtés des équipes, les conditions d'une performance durable.",
        "Nous n'imposons pas une transformation. Nous la rendons possible. Lorsque les bases tiennent et que la clarté revient, une ambition nouvelle peut émerger : plus réaliste, plus forte, plus durable. Parce que l'industrie européenne mérite des partenaires capables d'opérer là où la pression est réelle, avec maîtrise, humilité et un engagement constant envers le temps long."
      ]
    : [
        "Where systems are under strain. Where constraints compound. Where every decision carries real weight.",
        "These environments demand precision, clear-headedness, and a genuine understanding of what's happening on the ground.",
        "This is where Ocean Peak Group operates. We act with the accountability of a shareholder and the experience of operational teams who have run factories, stabilised supply chains, and navigated through crisis. That experience doesn't give us certainty. It gives us a way of approaching organisations with respect, pragmatism, and precision.",
        "Our role: to make sense of what's hard to read, restore clarity, strengthen the fundamentals, and build, alongside the teams, the conditions for lasting performance.",
        "We don't impose transformation. We make it possible. When the foundations hold and clarity returns, a new kind of ambition can take shape. More grounded, more resilient, more enduring. Because European industry deserves partners willing to operate where the pressure is real, with mastery, humility, and an unwavering commitment to the long term."
      ];

  const introText = language === 'fr'
    ? "Dans l'industrie, l'essentiel se joue en profondeur."
    : "In industry, the things that matter most happen below the surface.";

  const values = [
    {
      title: language === 'fr' ? 'Exigence' : 'Excellence',
      description: language === 'fr'
        ? 'Recherche permanente du niveau le plus élevé de qualité, de précision et de performance.'
        : 'A relentless pursuit of the highest standards in quality, precision, and performance.'
    },
    {
      title: language === 'fr' ? 'Rigueur opérationnelle' : 'Operational Rigour',
      description: language === 'fr'
        ? 'Décisions fondées sur les faits, la maîtrise des systèmes et l\'analyse technique.'
        : 'Decisions grounded in facts, systems mastery, and technical analysis.'
    },
    {
      title: language === 'fr' ? 'Loyauté' : 'Loyalty',
      description: language === 'fr'
        ? 'Relation fiable et engagée envers les dirigeants, les équipes et l\'entreprise.'
        : 'A dependable, committed relationship with leaders, teams, and the business.'
    },
    {
      title: language === 'fr' ? 'Maîtrise technique' : 'Technical Mastery',
      description: language === 'fr'
        ? 'Compréhension profonde des métiers, des procédés industriels et des contraintes de terrain.'
        : 'A deep understanding of the trades, industrial processes, and real-world constraints.'
    },
    {
      title: language === 'fr' ? 'Courage' : 'Courage',
      description: language === 'fr'
        ? 'Capacité à affronter les situations complexes, à dire la vérité et à prendre les décisions difficiles.'
        : 'The willingness to face complex situations, to speak the truth, and to make the hard calls.'
    },
    {
      title: language === 'fr' ? 'Engagement de long terme' : 'Long-Term Commitment',
      description: language === 'fr'
        ? 'Construire des trajectoires pérennes, avec constance, stabilité et responsabilité.'
        : 'Building sustainable trajectories with consistency, stability, and accountability.'
    },
    {
      title: language === 'fr' ? 'Transmission' : 'Knowledge Transfer',
      description: language === 'fr'
        ? 'Renforcement des équipes par le partage des savoir-faire et des pratiques d\'excellence.'
        : 'Strengthening teams through the sharing of expertise and best-in-class practices.'
    }
  ];

  useEffect(() => {
    // Scroll to top and reset styles before creating new ScrollTriggers
    window.scrollTo(0, 0);
    if (introTextRef.current) {
      gsap.set(introTextRef.current, { filter: 'blur(0px)', opacity: 1 });
    }
    if (contentWrapperRef.current) {
      gsap.set(contentWrapperRef.current, { filter: 'blur(12px)', opacity: 0 });
    }

    // Small delay to let scroll position settle before creating triggers
    const timer = setTimeout(() => {
      // Parallax effect for banner image
      if (imageRef.current) {
        gsap.to(imageRef.current, {
          yPercent: 20,
          ease: 'none',
          scrollTrigger: {
            trigger: bannerRef.current,
            start: 'top top',
            end: 'bottom top',
            scrub: 1
          }
        });
      }

      // Intro text blur - fast, as soon as scrolling past, reverses on scroll up
      gsap.fromTo(introTextRef.current,
        { filter: 'blur(0px)', opacity: 1 },
        {
          filter: 'blur(15px)',
          opacity: 0,
          ease: 'none',
          scrollTrigger: {
            trigger: introSectionRef.current,
            start: 'bottom 65%',
            end: 'bottom 35%',
            scrub: 0.2,
          }
        }
      );

      // Content blur-in reveal - text starts blurred, clears as you scroll into it
      gsap.fromTo(contentWrapperRef.current,
        { filter: 'blur(12px)', opacity: 0 },
        {
          filter: 'blur(0px)',
          opacity: 1,
          ease: 'none',
          scrollTrigger: {
            trigger: contentWrapperRef.current,
            start: 'top 80%',
            end: 'top 40%',
            scrub: 0.2,
          }
        }
      );
    }, 50);

    return () => {
      clearTimeout(timer);
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, [language]);

  const ctaButtons = [
    { label: language === 'fr' ? 'INVESTIR AVEC NOUS' : 'INVEST WITH US', link: '/contact?type=invest' },
    { label: language === 'fr' ? 'TRAVAILLER AVEC NOUS' : 'WORK WITH US', link: '/contact?type=work' },
    { label: language === 'fr' ? 'DEVENIR PARTENAIRE' : 'PARTNER WITH US', link: '/contact?type=partner' }
  ];

  return (
    <div className="min-h-screen" style={{ overflowX: 'hidden', background: '#ffffff' }}>
      {/* Floating CTA Buttons */}
      <div className="fixed right-8 top-1/3 z-40 flex flex-col gap-3 hidden lg:flex">
        {ctaButtons.map((btn, idx) => (
          <Link
            key={idx}
            to={btn.link}
            className="text-white text-xs font-bold tracking-wider px-5 py-3 bg-light-blue hover:bg-abyssal-blue transition-all duration-300 shadow-lg whitespace-nowrap"
            style={{ fontFamily: "'Novecento Sans Wide', 'Arial', sans-serif", letterSpacing: '1px', fontSize: '10px' }}
          >
            {btn.label}
          </Link>
        ))}
      </div>

      {/* Hero Banner with Image */}
      <section
        ref={bannerRef}
        className="relative h-[500px] flex flex-col overflow-hidden"
        style={{ backgroundColor: '#0a1f35' }}
      >
        <div
          ref={imageRef}
          className="absolute inset-0 bg-cover bg-center scale-110"
          style={{
            backgroundImage: 'url(/hero-approach.jpg)'
          }}
        />
        <div className="absolute inset-0 bg-abyssal-blue/50" />
        <div className="relative z-10 flex-1 flex flex-col items-center justify-center px-4">
          <h1
            className="text-5xl lg:text-6xl text-white mb-8 text-center"
            style={{
              fontFamily: "'Bank Gothic', 'Impact', sans-serif",
              fontWeight: 900,
              letterSpacing: '0.02em',
              textTransform: 'uppercase'
            }}
          >
            {language === 'fr' ? 'NOTRE PHILOSOPHIE' : 'OUR PHILOSOPHY'}
          </h1>
          <p
            className="text-xl lg:text-2xl text-white text-center max-w-4xl mx-auto font-bold"
            style={{
              fontFamily: "'Eurostile', 'Arial', sans-serif",
              lineHeight: 1.6
            }}
          >
            {language === 'fr'
              ? "Là où la pression est réelle, l'engagement doit l'être aussi."
              : "Where the pressure is real, so must the commitment be."}
          </p>
        </div>
      </section>

      {/* Intro text on white background - less padding to sit higher */}
      <section
        ref={introSectionRef}
        style={{
          background: '#ffffff',
          padding: '8vh 10%',
          textAlign: 'center',
        }}
      >
        <p
          ref={introTextRef}
          style={{
            fontFamily: "'Eurostile', 'Arial', sans-serif",
            fontSize: '1.75rem',
            lineHeight: 1.9,
            color: '#1e3a5f',
            fontWeight: 700,
            maxWidth: '800px',
            margin: '0 auto',
            willChange: 'filter, opacity',
          }}
        >
          {introText}
        </p>
      </section>

      {/* Gradient blend from white → blue (curtain falling effect) */}
      <div
        ref={transitionRef}
        style={{
          background: 'linear-gradient(180deg, #ffffff 0%, rgba(10, 31, 53, 0.15) 20%, rgba(10, 31, 53, 0.5) 40%, rgba(10, 31, 53, 0.85) 60%, #0a1f35 80%)',
          height: '40vh',
          width: '100%',
        }}
      />

      {/* Blue section - scrollable body text, content blurs in */}
      <section
        style={{
          background: '#0a1f35',
          padding: '5vh 10% 10vh',
          textAlign: 'center',
        }}
      >
        <div
          ref={contentWrapperRef}
          style={{
            maxWidth: '900px',
            margin: '0 auto',
            filter: 'blur(12px)',
            opacity: 0,
            willChange: 'filter, opacity',
          }}
        >
          {/* Body text */}
          <div
            style={{
              fontFamily: "'Eurostile', 'Arial', sans-serif",
              color: '#ffffff',
              fontSize: '1.125rem',
              lineHeight: 1.8,
              fontWeight: 400,
              textAlign: 'center',
            }}
          >
            {textParagraphs.map((paragraph, idx) => (
              <p key={idx} style={{ marginBottom: '1.5rem', fontSize: '1.125rem', lineHeight: 1.8, fontWeight: 400 }}>
                {paragraph}
              </p>
            ))}

            {/* Tagline */}
            <p
              style={{
                fontStyle: 'italic',
                fontWeight: 700,
                fontFamily: "'Eurostile', 'Arial', sans-serif",
                marginTop: '3rem',
                fontSize: '1.75rem',
                color: '#ffffff',
              }}
            >
              Into the Depths, Beyond the Expected.
            </p>
          </div>
        </div>
      </section>

      {/* Gradient blend from blue → white (same curtain speed as above) */}
      <div
        ref={valuesTransitionRef}
        style={{
          background: 'linear-gradient(180deg, #0a1f35 0%, rgba(10, 31, 53, 0.85) 20%, rgba(10, 31, 53, 0.5) 40%, rgba(10, 31, 53, 0.15) 60%, #f4f5f7 80%)',
          height: '40vh',
          width: '100%',
        }}
      />

      {/* Values Section */}
      <section
        style={{
          position: 'relative',
          backgroundColor: '#f4f5f7',
          padding: '4rem 2rem 8rem',
        }}
      >
        <div style={{ maxWidth: '1000px', width: '100%', margin: '0 auto', textAlign: 'center' }}>
          <h2
            className="text-abyssal-blue mb-16"
            style={{
              fontFamily: "'Bank Gothic', 'Impact', sans-serif",
              fontSize: 'clamp(2.5rem, 5vw, 4rem)',
              fontWeight: 900,
              letterSpacing: '0.02em',
              textTransform: 'uppercase',
            }}
          >
            {language === 'fr' ? 'NOS VALEURS' : 'OUR VALUES'}
          </h2>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '3rem' }}>
            {values.map((value, idx) => (
              <ValueCard
                key={idx}
                title={value.title}
                description={value.description}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Bottom padding */}
      <div style={{ backgroundColor: '#f4f5f7', height: '80px' }} />
    </div>
  );
};
